import { FORMAT_VERSION } from '../config.ts';

export const MAGIC = new TextEncoder().encode('ZCRY');
export const HEADER_AAD_LEN = 120;
export const FIXED_HEADER_LEN = 120;
export const WRAP_NONCE_LEN = 12;
export const WRAPPED_CEK_LEN = 48; // 32 ct + 16 tag
export const FILE_ID_LEN = 8;
export const SALT_LEN = 16;
export const GCM_TAG_LEN = 16;
export const CEK_LEN = 32;
export const KEK_LEN = 32;

export const KDF_ARGON2ID = 1;
export const CIPHER_AES_256_GCM = 1;

export const CHUNK_SIZE_DEFAULT = 1024 * 1024; // 1 MiB
export const CHUNK_SIZE_MIN = 64 * 1024;
export const CHUNK_SIZE_MAX = 8 * 1024 * 1024;

export const PLAINTEXT_MAX = 5n * 1024n * 1024n * 1024n * 1024n; // 5 TiB
export const ENC_META_MAX = 16 * 1024;
export const META_PLAINTEXT_MAX = 8192;
export const META_NAME_MAX = 1024;
export const META_NOTE_MAX = 4096;

export const ARGON2_MEMORY_DEFAULT = 65536; // KiB
export const ARGON2_ITERATIONS_DEFAULT = 3;
export const ARGON2_PARALLELISM_DEFAULT = 1;

export const ARGON2_MEMORY_MIN = 19_456;
export const ARGON2_MEMORY_MAX = 524_288;
export const ARGON2_ITERATIONS_MIN = 1;
export const ARGON2_ITERATIONS_MAX = 64;
export const ARGON2_PARALLELISM_MIN = 1;
export const ARGON2_PARALLELISM_MAX = 4;
/** Reject if memory_kib * parallelism exceeds this KiB-equivalent budget. */
export const ARGON2_MEM_PARALLEL_MAX = 1024 * 1024; // 1 GiB KiB units

export type ContainerHeader = {
  formatVersion: number;
  flags: number;
  kdfId: number;
  cipherId: number;
  argon2MemoryKib: number;
  argon2Iterations: number;
  argon2Parallelism: number;
  chunkSize: number;
  plaintextLength: bigint;
  fileId: Uint8Array;
  salt: Uint8Array;
  wrapNonce: Uint8Array;
  wrappedCek: Uint8Array;
};

export type FileMetadata = {
  name?: string;
  mime?: string;
  note?: string;
  created?: string;
};

export class FormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FormatError';
  }
}

export class CryptoAuthError extends Error {
  constructor(
    message = 'Decryption failed (wrong password or corrupted file)',
  ) {
    super(message);
    this.name = 'CryptoAuthError';
  }
}

export function assertChunkSize(chunkSize: number): void {
  if (
    !Number.isInteger(chunkSize) ||
    chunkSize < CHUNK_SIZE_MIN ||
    chunkSize > CHUNK_SIZE_MAX
  ) {
    throw new FormatError('Invalid chunk size');
  }
}

export function assertArgon2Params(
  memoryKib: number,
  iterations: number,
  parallelism: number,
): void {
  if (
    !Number.isInteger(memoryKib) ||
    memoryKib < ARGON2_MEMORY_MIN ||
    memoryKib > ARGON2_MEMORY_MAX
  ) {
    throw new FormatError('Rejected Argon2 memory parameter');
  }
  if (
    !Number.isInteger(iterations) ||
    iterations < ARGON2_ITERATIONS_MIN ||
    iterations > ARGON2_ITERATIONS_MAX
  ) {
    throw new FormatError('Rejected Argon2 iteration parameter');
  }
  if (
    !Number.isInteger(parallelism) ||
    parallelism < ARGON2_PARALLELISM_MIN ||
    parallelism > ARGON2_PARALLELISM_MAX
  ) {
    throw new FormatError('Rejected Argon2 parallelism parameter');
  }
  if (memoryKib * parallelism > ARGON2_MEM_PARALLEL_MAX) {
    throw new FormatError('Rejected Argon2 memory×parallelism budget');
  }
}

export function dataChunkCount(plaintextLength: bigint, chunkSize: number): number {
  if (plaintextLength === 0n) return 0;
  const cs = BigInt(chunkSize);
  const n = (plaintextLength + cs - 1n) / cs;
  if (n > 0xffffffffn) {
    throw new FormatError('Chunk counter would overflow');
  }
  return Number(n);
}

export function expectedContainerLength(
  plaintextLength: bigint,
  chunkSize: number,
  encMetaLen: number,
): number {
  const chunks = dataChunkCount(plaintextLength, chunkSize);
  // header 120 + enc_meta_len(4) + enc_meta + tag + sum(ct+tag per chunk)
  let total = FIXED_HEADER_LEN + 4 + encMetaLen + GCM_TAG_LEN;
  if (plaintextLength === 0n) return total;
  const fullChunks = chunks - 1;
  const lastPlain =
    Number(plaintextLength % BigInt(chunkSize) === 0n
      ? BigInt(chunkSize)
      : plaintextLength % BigInt(chunkSize));
  total += fullChunks * (chunkSize + GCM_TAG_LEN) + (lastPlain + GCM_TAG_LEN);
  return total;
}

export function validateMetadata(meta: FileMetadata): void {
  const json = JSON.stringify(meta);
  if (new TextEncoder().encode(json).length > META_PLAINTEXT_MAX) {
    throw new FormatError('Metadata too large');
  }
  if (meta.name && new TextEncoder().encode(meta.name).length > META_NAME_MAX) {
    throw new FormatError('Filename metadata too long');
  }
  if (meta.note && new TextEncoder().encode(meta.note).length > META_NOTE_MAX) {
    throw new FormatError('Note metadata too long');
  }
}

export { FORMAT_VERSION };
